import { useAuth } from '@/components/auth/authProvider';
import { isInternalUser } from '@/components/auth/RequireIsInternalUser';
import { MainContainer } from '@/components/MainContainer';
import { useGetOrganizationDetailsQuery, useGetOrganizationReviewSlaSettingsQuery } from '@/services/organizations';
import { Box } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Helmet } from 'react-helmet';
import { Link, NavLink, Route, Routes, useParams } from 'react-router-dom';

import { UserPermissions } from 'types';

import usePermissions from '../../../hooks/usePermissions';
import RequirePalmettoFinanceUser from '../../auth/RequirePalmettoFinanceUser';
import { isPalmettoFinanceUser } from '../../auth/RequirePalmettoFinanceUser';
import RequirePermissions from '../../auth/requirePermissions';
import PageHeader from '../../PageHeader';

import { Capacity } from './Capacity';
import Webhooks from './Developer/Webhooks';
import DirectPay from './DirectPay/DirectPay';
import Emails from './Emails/Emails';
import History from './History';
import { HoldbackSelfService } from './HoldbackSelfService/HoldbackSelfService';
import { MaintenanceSettings } from './Maintenance/MaintenanceSettings';
import OrganizationSettings from './OrganizationSettings';
import { OrganizationStateLicenses } from './OrganizationStateLicenses';
import { Payments } from './Payments/Payments';
import { Pricing } from './Pricing/Pricing';
import { QCPriority } from './QCPriority/QCPriority';
import { Underwriting } from './Underwriting/Underwriting';

export function OrganizationDetails() {
  const params = useParams<{ alias: string; programType: string }>();

  const { data: organization, isLoading } = useGetOrganizationDetailsQuery({ alias: params.alias as string });
  const {
    enablePayouts = false,
    developerSettingsTab = false,
    enableHoldbackSelfService = false,
    orgCoverageAreas = false,
    enableQcPriority = false,
    maintenanceSettingsTab = false,
  } = useFlags();
  const permissions = usePermissions();
  const { claims } = useAuth();

  const showHoldbackSettingsTab =
    enableHoldbackSelfService &&
    permissions?.some((permission) =>
      [UserPermissions.lightReachOrganizationSignatory, UserPermissions.orgAccountingAdmin].includes(permission),
    );

  const showCapacityTab =
    orgCoverageAreas &&
    (isPalmettoFinanceUser(claims) || permissions?.includes(UserPermissions.leadManagementAdmin));

  // lightReachAccountingAdmin/lightReachAccountingReader/lightReachAccountingSettings/lightReachAccountingApprover/orgAccountingAdmin can always see tab if FF is enabled
  // others only see the tab if a document exists
  const showPaymentsTab =
    enablePayouts &&
    permissions?.some((permission) =>
      [
        UserPermissions.lightReachAccountingAdmin,
        UserPermissions.lightReachAccountingReader,
        UserPermissions.lightReachAccountingSettings,
        UserPermissions.lightReachAccountingApprover,
        UserPermissions.orgAccountingAdmin,
        UserPermissions.lightReachOrganizationSignatory,
      ].includes(permission),
    );

  const showUnderwritingTab =
    (permissions?.includes(UserPermissions.lightReachSupport) ||
      (isInternalUser(claims, true) && permissions?.includes(UserPermissions.lightReachFicoAdmin))) ??
    false;

  const hasQcPriorityAccess =
    enableQcPriority &&
    (permissions?.some((p) => [UserPermissions.admin, UserPermissions.lightReachQCSuperAdmin].includes(p)) ?? false);

  // QC super admins manage the settings and can reach the tab even when QC priority is disabled.
  // Everyone else may only see the tab when the org's QC priority is actually enabled.
  const canManageQcPriority = permissions?.includes(UserPermissions.lightReachQCSuperAdmin) ?? false;
  const firstProgramType = organization?.programTypes?.[0];
  const { data: reviewSlaData } = useGetOrganizationReviewSlaSettingsQuery(
    { alias: params.alias as string, programType: firstProgramType },
    { skip: !hasQcPriorityAccess || canManageQcPriority || !firstProgramType },
  );

  const showQcPriorityTab =
    hasQcPriorityAccess && (canManageQcPriority || (reviewSlaData?.setting?.value?.enabled ?? false));

  const showMaintenanceSettingsTab = maintenanceSettingsTab && isInternalUser(claims, true);

  return (
    <>
      <Helmet>
        <title>{isLoading ? '...' : organization?.name || 'organization'} | Organizations | Settings</title>
      </Helmet>
      <MainContainer>
        <PageHeader
          title={isLoading ? '...' : organization?.name || 'organization'}
          eyebrow={<Link to="/settings/organizations">Organizations</Link>}
        />

        <Box direction="row" borderWidth="0 0 xs 0" borderColor="separator">
          <NavLink
            to={`/settings/organizations/${params.alias}`}
            style={{ textDecoration: 'none', fontWeight: '500' }}
            end
          >
            {({ isActive }) => (
              <Box
                as="span"
                padding="md"
                className={isActive ? 'tab-active' : ''}
                hover={{ color: 'primary-500' }}
                style={{ transitionDuration: '0s' }}
              >
                Settings
              </Box>
            )}
          </NavLink>
          <NavLink
            to={`/settings/organizations/${params.alias}/licenses?programType=${organization?.programTypes?.[0]}`}
            style={{ textDecoration: 'none', fontWeight: '500' }}
          >
            {({ isActive }) => (
              <Box
                as="span"
                padding="md"
                className={isActive ? 'tab-active' : ''}
                hover={{ color: 'primary-500' }}
                style={{ transitionDuration: '0s' }}
              >
                Licenses
              </Box>
            )}
          </NavLink>
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.admin]}>
              <NavLink
                to={`/settings/organizations/${params.alias}/pricing/${organization?.programTypes?.[0]}`}
                style={{ textDecoration: 'none', fontWeight: '500' }}
              >
                {({ isActive }) => (
                  <Box
                    as="span"
                    padding="md"
                    className={isActive ? 'tab-active' : ''}
                    hover={{ color: 'primary-500' }}
                    style={{ transitionDuration: '0s' }}
                  >
                    Pricing
                  </Box>
                )}
              </NavLink>
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
          {showPaymentsTab && (
            <RequirePermissions
              permissions={[
                UserPermissions.admin,
                UserPermissions.orgAccountingAdmin,
                UserPermissions.lightReachOrganizationSignatory,
              ]}
              checkAllPermissions={false}
            >
              <NavLink
                to={`/settings/organizations/${params.alias}/payments`}
                style={{ textDecoration: 'none', fontWeight: '500' }}
              >
                {({ isActive }) => (
                  <Box
                    as="span"
                    padding="md"
                    className={isActive ? 'tab-active' : ''}
                    hover={{ color: 'primary' }}
                    style={{ transitionDuration: '0s' }}
                  >
                    Payments
                  </Box>
                )}
              </NavLink>
            </RequirePermissions>
          )}
          {showHoldbackSettingsTab && (
            <NavLink
              to={`/settings/organizations/${params.alias}/holdback-settings/${organization?.programTypes?.[0]}`}
              style={{ textDecoration: 'none', fontWeight: '500' }}
            >
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s' }}
                >
                  Holdback Settings
                </Box>
              )}
            </NavLink>
          )}
          <NavLink
            to={`/settings/organizations/${params.alias}/emails`}
            style={{ textDecoration: 'none', fontWeight: '500' }}
          >
            {({ isActive }) => (
              <Box
                as="span"
                padding="md"
                className={isActive ? 'tab-active' : ''}
                hover={{ color: 'primary' }}
                style={{ transitionDuration: '0s' }}
              >
                Emails
              </Box>
            )}
          </NavLink>
          {showCapacityTab && (
            <NavLink
              to={`/settings/organizations/${params.alias}/capacity`}
              style={{ textDecoration: 'none', fontWeight: '500' }}
            >
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s' }}
                >
                  Capacity
                </Box>
              )}
            </NavLink>
          )}
          {developerSettingsTab && (
            <RequirePermissions permissions={[UserPermissions.admin]}>
              <NavLink
                to={`/settings/organizations/${params.alias}/developer`}
                style={{ textDecoration: 'none', fontWeight: '500' }}
              >
                {({ isActive }) => (
                  <Box
                    as="span"
                    padding="md"
                    className={isActive ? 'tab-active' : ''}
                    hover={{ color: 'primary' }}
                    style={{ transitionDuration: '0s' }}
                  >
                    Developer Settings
                  </Box>
                )}
              </NavLink>
            </RequirePermissions>
          )}
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.admin]}>
              <NavLink
                to={`/settings/organizations/${params.alias}/direct-pay`}
                style={{ textDecoration: 'none', fontWeight: '500' }}
                end
              >
                {({ isActive }) => (
                  <Box
                    as="span"
                    padding="md"
                    className={isActive ? 'tab-active' : ''}
                    hover={{ color: 'primary-500' }}
                    style={{ transitionDuration: '0s' }}
                  >
                    Direct Pay
                  </Box>
                )}
              </NavLink>
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
          {showUnderwritingTab && (
            <RequirePalmettoFinanceUser>
              <NavLink
                to={`/settings/organizations/${params.alias}/underwriting`}
                style={{ textDecoration: 'none', fontWeight: '500' }}
              >
                {({ isActive }) => (
                  <Box
                    as="span"
                    padding="md"
                    className={isActive ? 'tab-active' : ''}
                    hover={{ color: 'primary-500' }}
                    style={{ transitionDuration: '0s' }}
                  >
                    Underwriting
                  </Box>
                )}
              </NavLink>
            </RequirePalmettoFinanceUser>
          )}
          {showQcPriorityTab && (
            <NavLink
              to={`/settings/organizations/${params.alias}/qc-priority`}
              style={{ textDecoration: 'none', fontWeight: '500' }}
            >
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary-500' }}
                  style={{ transitionDuration: '0s' }}
                >
                  QC Priority
                </Box>
              )}
            </NavLink>
          )}
          {showMaintenanceSettingsTab && (
            <RequirePalmettoFinanceUser>
              <RequirePermissions permissions={[UserPermissions.admin]}>
                <NavLink
                  to={`/settings/organizations/${params.alias}/maintenance-settings`}
                  style={{ textDecoration: 'none', fontWeight: '500' }}
                >
                  {({ isActive }) => (
                    <Box
                      as="span"
                      padding="md"
                      className={isActive ? 'tab-active' : ''}
                      hover={{ color: 'primary-500' }}
                      style={{ transitionDuration: '0s' }}
                    >
                      Maintenance
                    </Box>
                  )}
                </NavLink>
              </RequirePermissions>
            </RequirePalmettoFinanceUser>
          )}
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.admin]}>
              <NavLink
                to={`/settings/organizations/${params.alias}/history`}
                style={{ textDecoration: 'none', fontWeight: '500' }}
                end
              >
                {({ isActive }) => (
                  <Box
                    as="span"
                    padding="md"
                    className={isActive ? 'tab-active' : ''}
                    hover={{ color: 'primary-500' }}
                    style={{ transitionDuration: '0s' }}
                  >
                    History
                  </Box>
                )}
              </NavLink>
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        </Box>

        <Routes>
          <Route index element={<OrganizationSettings organization={organization} isLoading={isLoading} />} />
          {showPaymentsTab && organization && (
            <Route path="payments/:paymentsType?/*" element={<Payments organization={organization} />} />
          )}
          {showHoldbackSettingsTab && organization && (
            <Route
              path="holdback-settings/:programType/:holdbackType?/*"
              element={<HoldbackSelfService organization={organization} />}
            />
          )}

          <Route path="pricing/:programType/:pricingType?/*" element={<Pricing organization={organization} />} />
          {organization && <Route path="/licenses" element={<OrganizationStateLicenses />} />}
          {organization && <Route path="/emails" element={<Emails organization={organization} />} />}
          {organization && <Route path="/developer" element={<Webhooks organization={organization} />} />}
          {organization && <Route path="/direct-pay" element={<DirectPay organization={organization} />} />}
          {organization && <Route path="/history" element={<History />} />}
          {showCapacityTab && organization && <Route path="/capacity" element={<Capacity />} />}
          {showQcPriorityTab && organization && (
            <Route path="/qc-priority" element={<QCPriority organization={organization} />} />
          )}
          {showMaintenanceSettingsTab && organization && (
            <Route
              path="/maintenance-settings"
              element={
                <RequirePalmettoFinanceUser>
                  <MaintenanceSettings organization={organization} />
                </RequirePalmettoFinanceUser>
              }
            />
          )}
          {organization && showUnderwritingTab && (
            <Route
              path="/underwriting"
              element={
                <RequirePalmettoFinanceUser>
                  <Underwriting organization={organization} />
                </RequirePalmettoFinanceUser>
              }
            />
          )}
        </Routes>
      </MainContainer>
    </>
  );
}
